import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Row, H2, Button, Subheading, LabelTooltip } from '@fs/zion-ui'
import { PersonFamily } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import {
  DISABLED_BACKGROUND_COLOR,
  DISABLED_FOREGROUND_COLOR,
  REVIEWED_COLOR,
  IN_PROGRESS_COLOR,
} from '../../shared/utils/panelConstants'

/**
 * Title, subheading and Edit button for the Relationships card. Icon background reflects state:
 *  - Gray: Not yet reviewed (pending).
 *  - Green: Reviewed and not currently editing.
 *  - Yellow: In edit mode.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.setIsEditing Function to toggle edit state in the parent card.
 * @param {boolean} props.isEditing     Whether the parent card is in edit mode.
 * @param {boolean} [props.reviewed=false] Indicates if the section has been marked reviewed.
 * @param {boolean} [props.disableEdit=false] Disables editing (e.g. a restricted field).
 * @param {boolean} [props.noGroup=false] Whether the person is in no group at all.
 * @returns {JSX.Element}
 */
function RelationshipsHeader({ setIsEditing, isEditing, reviewed = false, disableEdit = false, noGroup = false }) {
  const [t] = useTranslation()
  let iconColor = DISABLED_BACKGROUND_COLOR
  if (reviewed && !isEditing) {
    iconColor = REVIEWED_COLOR
  }
  if (isEditing) {
    iconColor = IN_PROGRESS_COLOR
  }

  const handleEditClick = useCallback(() => {
    // Suppressed in JS rather than via the disabled attribute, so the control stays focusable.
    if (noGroup) return
    setIsEditing(true)
  }, [noGroup, setIsEditing])

  const editButton = (
    <Button
      onClick={handleEditClick}
      linkName="household-details-edit-btn"
      disabled={disableEdit}
      aria-disabled={noGroup || undefined}
      data-testid="relationships-edit-button"
    >
      {t('household-details-card.edit.btn', 'Edit')}
    </Button>
  )

  return (
    <Row gutters="xxs" alignItemsY="middle">
      <PersonFamily
        size="sm"
        background={iconColor}
        color={!isEditing && !reviewed ? DISABLED_FOREGROUND_COLOR : undefined}
      />
      <H2 growX size="H5">
        {t('relationships-card.title', 'Relationships')}
        {(!reviewed || isEditing) && !noGroup && (
          <Subheading>{t('relationships-card.subheading', 'Relationships for group members.')}</Subheading>
        )}
      </H2>

      {reviewed &&
        !isEditing &&
        (noGroup ? (
          <LabelTooltip
            triggerOnFocus
            label={t(
              'relationships-card.edit-disabled-tooltip',
              'Person needs to be in a group to edit relationships.'
            )}
            target={editButton}
          />
        ) : (
          editButton
        ))}
    </Row>
  )
}

RelationshipsHeader.propTypes = {
  setIsEditing: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  reviewed: PropTypes.bool,
  disableEdit: PropTypes.bool,
  noGroup: PropTypes.bool,
}

export default RelationshipsHeader
